import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PMField } from "../../../utils/PMField";
import { PMButton } from "../../../utils/PMButton";
import PMSwitch from "../../../utils/PMSwitch";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { formatPrice, parsePrice, calculateExclVat, calculateInclVat } from "../../../../utils/formatPrice";
import axios from "../../../../api/axios";
import { fetchProjectActivities } from "../../../../api/project_activity";

export default function ReportPriceLinesList({
  priceLines = [],
  project = null,
  vatTypes = [],
  onPriceLinesChange,
  disabled = false,
}) {
  const { t } = useTranslation();
  const [localPriceLines, setLocalPriceLines] = useState(priceLines || []);
  const [localVatTypes, setLocalVatTypes] = useState(vatTypes || []);
  const [projectActivities, setProjectActivities] = useState([]);
  
  useEffect(() => {
    if (vatTypes && vatTypes.length > 0) {
      setLocalVatTypes(vatTypes);
    } else {
      // Fetch vat_types if not provided as prop
      fetchVatTypes();
    }
  }, [vatTypes]);
  
  const fetchVatTypes = async () => {
    try {
      const response = await axios.get(`/vat_types`);
      const types = response.data || [];
      setLocalVatTypes(types);
    } catch (err) {
      console.error("Error fetching vat types:", err);
    }
  };

  React.useEffect(() => {
    setLocalPriceLines(priceLines || []);
  }, [priceLines]);

  useEffect(() => {
    if (!project?.id) {
      setProjectActivities([]);
      return;
    }
    let cancelled = false;
    fetchProjectActivities(project.id)
      .then((rows) => {
        if (!cancelled) setProjectActivities(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setProjectActivities([]);
      });
    return () => {
      cancelled = true;
    };
  }, [project?.id]);

  const handleAddPriceLine = () => {
    // Find default VAT type (type 1 - 25%)
    const defaultVatType = localVatTypes.find((vt) => vt.number === 1) || localVatTypes[0];
    
    const newPriceLine = {
      id: null,
      title: "",
      description: "",
      agreed_price: 0,
      agreed_price_incl_vat: 0,
      agreed_hours: 0,
      is_agreed_price_line: true,
      project_activity_id: null,
      vat_type_id: defaultVatType?.id || null,
      row_order: localPriceLines.length,
    };
    const updated = [...localPriceLines, newPriceLine];
    setLocalPriceLines(updated);
    onPriceLinesChange(updated);
  };

  const handleUpdatePriceLine = (index, field, value) => {
    const updated = [...localPriceLines];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    setLocalPriceLines(updated);
    onPriceLinesChange(updated);
  };

  const handleDeletePriceLine = (index) => {
    const updated = localPriceLines.filter((_, i) => i !== index);
    // Update row_order for remaining items
    updated.forEach((line, i) => {
      line.row_order = i;
    });
    setLocalPriceLines(updated);
    onPriceLinesChange(updated);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center">
        {!disabled && (
          <PMButton
            type="button"
            onClick={handleAddPriceLine}
            className="flex items-center gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            {t("report.price_lines.add")}
          </PMButton>
        )}
      </div>

      {!localPriceLines || localPriceLines.length === 0 ? (
        <div className="text-sm text-gray-500 italic p-4 border border-gray-200 rounded-lg">
          {t("report.price_lines.empty")}
        </div>
      ) : (
        <div className="space-y-4">
          {localPriceLines.map((priceLine, index) => {
            // Ensure priceLine exists
            if (!priceLine) return null;
            
            return (
            <div
              key={index}
              className="border border-gray-200 rounded-lg p-4 space-y-4"
            >
              <div className="flex justify-between items-start">
                <h4 className="text-md font-medium text-gray-700">
                  {t("report.price_lines.line")} {index + 1}
                </h4>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleDeletePriceLine(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-6 gap-4">
                <div className="col-span-6 sm:col-span-3">
                  <PMField
                    type="text"
                    label={t("report.price_lines.title_field")}
                    value={priceLine?.title || ""}
                    onChange={(e) =>
                      handleUpdatePriceLine(index, "title", e.target.value)
                    }
                    disabled={disabled}
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("report.price_lines.project_activity")}
                  </label>
                  <select
                    value={priceLine?.project_activity_id || ""}
                    onChange={(e) =>
                      handleUpdatePriceLine(
                        index,
                        "project_activity_id",
                        e.target.value ? parseInt(e.target.value, 10) : null
                      )
                    }
                    disabled={disabled || !project?.id}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">
                      {t("report.price_lines.no_activity")}
                    </option>
                    {projectActivities?.map((pa) => (
                      <option key={pa.id} value={pa.id}>
                        {pa.display_title || pa.title || `ID ${pa.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-span-6 sm:col-span-6">
                  <PMField
                    type="text"
                    multiline
                    rows={3}
                    label={t("report.price_lines.description_field")}
                    value={priceLine?.description || ""}
                    onChange={(e) =>
                      handleUpdatePriceLine(
                        index,
                        "description",
                        e.target.value
                      )
                    }
                    disabled={disabled}
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <PMSwitch
                    label={t("report.price_lines.is_agreed_price")}
                    checked={priceLine?.is_agreed_price_line !== false}
                    onChange={(e) =>
                      handleUpdatePriceLine(
                        index,
                        "is_agreed_price_line",
                        e.target.checked
                      )
                    }
                    disabled={disabled}
                  />
                </div>

                {priceLine?.is_agreed_price_line !== false && (
                  <>
                    <div className="col-span-6 sm:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("report.price_lines.vat_type")}
                      </label>
                      <select
                        value={priceLine?.vat_type_id || ""}
                        onChange={(e) => {
                          const vatTypeId = e.target.value ? parseInt(e.target.value) : null;
                          const vatType = vatTypes.find((vt) => vt.id === vatTypeId);
                          handleUpdatePriceLine(index, "vat_type_id", vatTypeId);
                          
                          // Recalculate prices when VAT type changes
                          if (vatType && priceLine?.agreed_price) {
                            const exclVat = parsePrice(formatPrice(priceLine.agreed_price));
                            const inclVat = calculateInclVat(exclVat, vatType.percentage);
                            handleUpdatePriceLine(index, "agreed_price_incl_vat", inclVat);
                          }
                        }}
                        disabled={disabled}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">{t("report.price_lines.no_vat_type")}</option>
                        {localVatTypes?.map((vatType) => (
                          <option key={vatType.id} value={vatType.id}>
                            {vatType.display_name || vatType.name} ({vatType.percentage}%)
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("report.price_lines.agreed_price_excl_vat")}
                      </label>
                      <input
                        type="text"
                        value={priceLine?.agreed_price ? formatPrice(priceLine.agreed_price) : "0,00"}
                        onChange={(e) => {
                          let value = e.target.value;
                          // Replace dot with comma
                          value = value.replace(".", ",");
                          // Only allow numbers, comma, and one comma
                          value = value.replace(/[^0-9,]/g, "");
                          // Ensure only one comma
                          const parts = value.split(",");
                          if (parts.length > 2) {
                            value = parts[0] + "," + parts.slice(1).join("");
                          }
                          
                          const exclVat = parsePrice(value);
                          handleUpdatePriceLine(index, "agreed_price", exclVat);
                          
                          // Calculate incl. VAT
                          const vatType = localVatTypes.find((vt) => vt.id === priceLine?.vat_type_id);
                          const vatPercentage = vatType?.percentage || 0;
                          const inclVat = calculateInclVat(exclVat, vatPercentage);
                          handleUpdatePriceLine(index, "agreed_price_incl_vat", inclVat);
                        }}
                        onBlur={(e) => {
                          const value = parsePrice(e.target.value);
                          handleUpdatePriceLine(index, "agreed_price", value);
                        }}
                        disabled={disabled}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0,00"
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("report.price_lines.agreed_price_incl_vat")}
                      </label>
                      <input
                        type="text"
                        value={priceLine?.agreed_price_incl_vat ? formatPrice(priceLine.agreed_price_incl_vat) : "0,00"}
                        onChange={(e) => {
                          let value = e.target.value;
                          // Replace dot with comma
                          value = value.replace(".", ",");
                          // Only allow numbers, comma, and one comma
                          value = value.replace(/[^0-9,]/g, "");
                          // Ensure only one comma
                          const parts = value.split(",");
                          if (parts.length > 2) {
                            value = parts[0] + "," + parts.slice(1).join("");
                          }
                          
                          const inclVat = parsePrice(value);
                          handleUpdatePriceLine(index, "agreed_price_incl_vat", inclVat);
                          
                          // Calculate excl. VAT
                          const vatType = localVatTypes.find((vt) => vt.id === priceLine?.vat_type_id);
                          const vatPercentage = vatType?.percentage || 0;
                          const exclVat = calculateExclVat(inclVat, vatPercentage);
                          handleUpdatePriceLine(index, "agreed_price", exclVat);
                        }}
                        onBlur={(e) => {
                          const value = parsePrice(e.target.value);
                          handleUpdatePriceLine(index, "agreed_price_incl_vat", value);
                        }}
                        disabled={disabled}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0,00"
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <PMField
                        type="number"
                        step="0.5"
                        label={t("report.price_lines.agreed_hours")}
                        value={priceLine?.agreed_hours || 0}
                        onChange={(e) =>
                          handleUpdatePriceLine(
                            index,
                            "agreed_hours",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        disabled={disabled}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

